import { useEffect, useRef, useState } from 'react'

export interface UseScrollRevealOptions {
  threshold?: number
  rootMargin?: string
  /** Fire once and stop observing (default) vs. toggle every time it crosses the viewport */
  once?: boolean
}

/**
 * Scroll-triggered entrance animation hook. Attach `ref` to the element and
 * read `inView` to drive an entrance transition — no manual
 * IntersectionObserver wiring per screen.
 */
export function useScrollReveal<T extends HTMLElement = HTMLDivElement>(
  options: UseScrollRevealOptions = {},
) {
  const { threshold = 0.2, rootMargin = '0px', once = true } = options
  const ref = useRef<T | null>(null)
  const [inView, setInView] = useState(false)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    if (typeof IntersectionObserver === 'undefined') {
      setInView(true)
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setInView(true)
            if (once) observer.disconnect()
          } else if (!once) {
            setInView(false)
          }
        })
      },
      { threshold, rootMargin },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [threshold, rootMargin, once])

  return { ref, inView }
}
